/**
 * Hook for managing compose mode state in the search bar.
 *
 * Integrates the PlayCallParser with vocabulary data to progressively
 * build a chip sequence as the coach types a play call string
 * (e.g., "3 I Z 44 Power").
 */

import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { PlayCallParser } from '@/services/PlayCallParser'
import type { VocabularyEntry } from '@/db/Types'
import type { PlayCallChip } from '@/types/BlockProperties'

export interface UseComposeModeProps {
	vocabulary: VocabularyEntry[]
	initialChips?: PlayCallChip[]
	onChipsChange?: (chips: PlayCallChip[]) => void
}

export interface UseComposeModeReturn {
	/** Confirmed chips in order */
	chips: PlayCallChip[]
	/** Current unresolved input text */
	pendingText: string
	/** Vocabulary suggestions for pending text */
	suggestions: VocabularyEntry[]
	/** Currently highlighted suggestion (-1 = none) */
	highlightedIndex: number
	/** True when at least one chip exists */
	isComposing: boolean
	/** Called on every input change */
	handleInputChange: (value: string) => void
	/** Space/Enter/Backspace/Arrow handling */
	handleKeyDown: (e: React.KeyboardEvent) => void
	/** Click on suggestion */
	handleSuggestionSelect: (entry: VocabularyEntry) => void
	/** Remove chip by index */
	removeChip: (index: number) => void
	/** Replace chip at index */
	replaceChip: (index: number, newChip: PlayCallChip) => void
	/** Reset all compose state */
	clearAll: () => void
	/** Derived: chip texts joined with spaces */
	playCallName: string
}

export function useComposeMode({ vocabulary, initialChips, onChipsChange }: UseComposeModeProps): UseComposeModeReturn {
	const parserRef = useRef(new PlayCallParser())

	const [chips, setChips] = useState<PlayCallChip[]>(initialChips ?? [])
	const [pendingText, setPendingText] = useState('')
	const [suggestions, setSuggestions] = useState<VocabularyEntry[]>([])
	const [highlightedIndex, setHighlightedIndex] = useState(-1)

	// Track serialized initialChips to detect external changes
	const prevInitialChipsRef = useRef(JSON.stringify(initialChips ?? []))

	// Reset chips when initialChips changes externally
	useEffect(() => {
		const serialized = JSON.stringify(initialChips ?? [])
		if (serialized !== prevInitialChipsRef.current) {
			prevInitialChipsRef.current = serialized
			setChips(initialChips ?? [])
		}
	}, [initialChips])

	// Notify parent when chips change (skip if chips match initialChips)
	const prevChipsRef = useRef(JSON.stringify(chips))
	useEffect(() => {
		const serialized = JSON.stringify(chips)
		if (serialized === prevChipsRef.current) return
		prevChipsRef.current = serialized

		// Skip callback if chips match initialChips to avoid save-on-load
		if (serialized === prevInitialChipsRef.current) return

		onChipsChange?.(chips)
	}, [chips, onChipsChange])

	const isComposing = useMemo(() => chips.length > 0, [chips])
	const playCallName = useMemo(() => chips.map(c => c.text).join(' '), [chips])

	const handleInputChange = useCallback((value: string) => {
		setPendingText(value)

		if (value.trim()) {
			const results = parserRef.current.suggest(value, vocabulary)
			setSuggestions(results)
		} else {
			setSuggestions([])
			setHighlightedIndex(-1)
		}
	}, [vocabulary])

	const handleSuggestionSelect = useCallback((entry: VocabularyEntry) => {
		setChips(prev => [
			...prev,
			{
				text: entry.code,
				vocabulary_id: entry.id,
				category: entry.category,
				order: prev.length,
			},
		])
		setPendingText('')
		setSuggestions([])
		setHighlightedIndex(-1)
	}, [])

	const removeChip = useCallback((index: number) => {
		setChips(prev =>
			prev
				.filter((_, i) => i !== index)
				.map((chip, i) => ({ ...chip, order: i }))
		)
	}, [])

	const replaceChip = useCallback((index: number, newChip: PlayCallChip) => {
		setChips(prev =>
			prev.map((chip, i) => i === index ? { ...newChip, order: i } : chip)
		)
	}, [])

	const clearAll = useCallback(() => {
		setChips([])
		setPendingText('')
		setSuggestions([])
		setHighlightedIndex(-1)
	}, [])

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		const parser = parserRef.current

		switch (e.key) {
			case ' ': {
				if (!pendingText.trim()) return

				const parseResult = parser.parse(pendingText.trim(), vocabulary)
				const resolvedTokens = parseResult.tokens.filter(t => t.isResolved)

				if (resolvedTokens.length > 0) {
					// Build chips from resolved tokens
					const newChips: PlayCallChip[] = resolvedTokens.map((token, i) => ({
						text: token.text,
						vocabulary_id: token.vocabularyEntry?.id ?? null,
						category: token.category,
						order: i, // Will be rebased below
					}))

					// Collect unresolved text
					const unresolvedTokens = parseResult.tokens.filter(t => !t.isResolved)
					const remaining = unresolvedTokens.map(t => t.text).join(' ')

					setChips(prev => {
						const rebased = newChips.map((chip, i) => ({
							...chip,
							order: prev.length + i,
						}))
						return [...prev, ...rebased]
					})

					setPendingText(remaining)
					if (remaining) {
						setSuggestions(parser.suggest(remaining, vocabulary))
					} else {
						setSuggestions([])
						setHighlightedIndex(-1)
					}

					e.preventDefault()
				}
				// If nothing resolves, let the space character be typed naturally
				break
			}

			case 'Enter': {
				if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
					// Select highlighted suggestion
					const entry = suggestions[highlightedIndex]!
					handleSuggestionSelect(entry)
					e.preventDefault()
					return
				}

				if (!pendingText.trim()) return

				// Force-resolve all pending text
				const parseResult = parser.parse(pendingText.trim(), vocabulary)
				const newChips: PlayCallChip[] = parseResult.tokens.map((token, i) => ({
					text: token.text,
					vocabulary_id: token.isResolved ? (token.vocabularyEntry?.id ?? null) : null,
					category: token.isResolved ? token.category : null,
					order: i, // Will be rebased below
				}))

				setChips(prev => {
					const rebased = newChips.map((chip, i) => ({
						...chip,
						order: prev.length + i,
					}))
					return [...prev, ...rebased]
				})

				setPendingText('')
				setSuggestions([])
				setHighlightedIndex(-1)
				e.preventDefault()
				break
			}

			case 'Backspace': {
				if (pendingText === '' && chips.length > 0) {
					setChips(prev => prev.slice(0, -1))
					e.preventDefault()
				}
				break
			}

			case 'ArrowDown': {
				if (suggestions.length > 0) {
					setHighlightedIndex(prev =>
						prev < suggestions.length - 1 ? prev + 1 : 0
					)
					e.preventDefault()
				}
				break
			}

			case 'ArrowUp': {
				if (suggestions.length > 0) {
					setHighlightedIndex(prev =>
						prev > 0 ? prev - 1 : suggestions.length - 1
					)
					e.preventDefault()
				}
				break
			}

			case 'Escape': {
				setSuggestions([])
				setHighlightedIndex(-1)
				e.preventDefault()
				break
			}

			case 'Tab': {
				if (suggestions.length > 0 && highlightedIndex >= 0) {
					const entry = suggestions[highlightedIndex]!
					handleSuggestionSelect(entry)
					e.preventDefault()
				}
				break
			}
		}
	}, [pendingText, vocabulary, highlightedIndex, suggestions, chips.length, handleSuggestionSelect])

	return {
		chips,
		pendingText,
		suggestions,
		highlightedIndex,
		isComposing,
		handleInputChange,
		handleKeyDown,
		handleSuggestionSelect,
		removeChip,
		replaceChip,
		clearAll,
		playCallName,
	}
}
